import { useQuery } from "@tanstack/react-query";
import { findAnnouncement, type AnnouncementId } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { isGoodMoment, readMoment } from "./announcement-moment";
import { hasAnnouncementContent } from "./announcement-content";

/** Let the page land first; a popup must never fight the first paint. */
const SETTLE_MS = 1500;

/**
 * At most one claim per page load. A query rather than an effect: TanStack
 * runs it once, caches the answer for the rest of the session (no refetch on
 * focus, reconnect or remount), and never retries. The moment is re-checked
 * right before claiming; if it is wrong, nothing is recorded and this load
 * simply shows nothing.
 */
export function useAnnouncementClaim(enabled: boolean) {
  const utils = trpc.useUtils();
  return useQuery({
    queryKey: ["announcements", "claim"],
    enabled,
    queryFn: async (): Promise<AnnouncementId | null> => {
      await new Promise((resolve) => setTimeout(resolve, SETTLE_MS));
      if (!isGoodMoment(readMoment())) return null;
      const id = await utils.client.announcements.claim.mutate();
      const announcement = id ? findAnnouncement(id) : undefined;
      return announcement && hasAnnouncementContent(announcement.id)
        ? announcement.id
        : null;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}
